import { Button } from '../../Utility/Button';
import Checkbox from '../../Utility/FormElements/Checkbox';
import Input from '../../Utility/FormElements/Input';
import Select from '../../Utility/FormElements/Select';
import AdminContainer from '../../Utility/AdminContainer';
import { LlmProvider } from '../../../Types';
import { DLPState } from '../../../Types/Enums';
import useModel from '../../../Hooks/useModel';
import AdminBox from '../../Utility/AdminBox';
import ToggleSwitch from '../../Utility/ToggleSwitch';
import ImageUpload from '../../Utility/ImageUpload';
import AdminBoxHeadline from '../../Utility/AdminBoxHeadline';
import TextareaAutosize from 'react-textarea-autosize';
import ReactSelect, { MultiValue, ActionMeta } from 'react-select';
import { Copy, Check } from '@phosphor-icons/react';

type MimeTypeOption = {
    value: string;
    label: string;
};

function Model() {
    const {
        ModelActions,
        modelSnapshot,
        model,
        imagePreview,
        icapAddress,
        i18n,
        llmProviders,
        sensitivityLabels,
        loadingState,
        modelIdentifier,
        additionalParamsText,
        jsonError,
        allMimeTypesWithFileExtensions,
        credentials,
        mimeTypesCopied,
    } = useModel();

    // Helper: verarbeitet Text (CSV, Zeilen, Semikolon) zu MimeTypes und merged sie in das Modell
    const handleMimePasteText = (text: string) => {
        const newMimeTypes = text
            .split(/[\n,;]+/)
            .map((mime) => mime.trim())
            .filter((mime) => mime.length > 0);
        if (newMimeTypes.length > 0) {
            const existingMimeTypes = model.allowedMimeTypes || [];
            const mergedMimeTypes = Array.from(new Set([...existingMimeTypes, ...newMimeTypes]));
            ModelActions.updateModel({ allowedMimeTypes: mergedMimeTypes });
        }
    };

    return (
        <div className={`admin-container flex relative flex-col`}>
            <AdminContainer>
                <AdminBox
                    contentLoaded={
                        loadingState.llmModelLoaded && loadingState.modelLoaded && loadingState.credentialsLoaded
                    }
                >
                    <div className="flex flex-col gap-5">
                        <AdminBoxHeadline
                            headLine={i18n.translate({
                                id: modelIdentifier === 'new' ? 'newModel' : 'editModel',
                                sourceName: 'Model',
                            })}
                            subLine={modelIdentifier === 'new' ? undefined : modelSnapshot?.title}
                        />

                        <ImageUpload
                            handleImageChange={(e) => {
                                ModelActions.handleImageChange(e);
                            }}
                            camRemoveImage={false}
                            imagePreview={imagePreview}
                        >
                            <Input
                                inputType="text"
                                value={model.title ?? ''}
                                onChange={(value) => ModelActions.handleChangeModel('title', value)}
                                placeholder={i18n.translate({ id: 'placeholder.title', sourceName: 'Model' })}
                                className="w-full"
                            />
                        </ImageUpload>

                        <div>
                            <label className="block mb-2">
                                {i18n.translate({ id: 'title.provider', sourceName: 'Model' })}
                            </label>
                            <Select
                                value={model.provider ?? '---'}
                                onChange={ModelActions.handleLlmProviderDropdownChange}
                            >
                                <option key="---" value="---">
                                    {i18n.translate({ id: 'option', sourceName: 'Main' })}
                                </option>
                                {llmProviders &&
                                    llmProviders.map((llmProvider: LlmProvider) => {
                                        return (
                                            <option key={llmProvider.identifier} value={llmProvider.identifier}>
                                                {i18n.translate({
                                                    id: 'llmProvider.' + llmProvider.identifier,
                                                    sourceName: 'Model',
                                                })}
                                            </option>
                                        );
                                    })}
                            </Select>
                        </div>

                        <div>
                            <label className="block mb-2">
                                {i18n.translate({ id: 'title.credentials', sourceName: 'Model' })}
                            </label>
                            <Select
                                value={model.credential?.identifier ?? '---'}
                                onChange={ModelActions.handleCredentialDropdownChange}
                            >
                                <option key="---" value="---">
                                    {i18n.translate({ id: 'option', sourceName: 'Main' })}
                                </option>
                                {credentials.map((credential) => (
                                    <option key={credential.identifier} value={credential.identifier}>
                                        {credential.name}
                                    </option>
                                ))}
                            </Select>
                        </div>
                        <div>
                            <label className="block mb-2">
                                {i18n.translate({ id: 'title.modelName', sourceName: 'Model' })}
                            </label>
                            <Input
                                inputType="text"
                                value={model.value ?? ''}
                                onChange={(value) => ModelActions.handleChangeModel('value', value)}
                                placeholder={i18n.translate({ id: 'placeholder.modelName', sourceName: 'Model' })}
                            />
                        </div>
                        <div>
                            <label className="block mb-2">
                                {i18n.translate({
                                    id: 'title.costOfInputTokens',
                                    sourceName: 'Model',
                                })}
                            </label>
                            <Input
                                inputType="number"
                                value={model.costInput ?? 0}
                                onChange={(value) => ModelActions.handleChangeModel('costInput', value)}
                                inputProps={{ step: 0.01 }}
                            />
                        </div>
                        <div>
                            <label className="block mb-2">
                                {i18n.translate({
                                    id: 'title.costOfOutputTokens',
                                    sourceName: 'Model',
                                })}
                            </label>
                            <Input
                                inputType="number"
                                value={model.costOutput ?? 0}
                                onChange={(value) => ModelActions.handleChangeModel('costOutput', value)}
                                inputProps={{ step: 0.01 }}
                            />
                        </div>
                        <div>
                            <label className="block mb-2">
                                {i18n.translate({ id: 'title.maxTokens', sourceName: 'Model' })}
                            </label>
                            <Input
                                inputType="number"
                                value={model.tokensPerContext ?? 0}
                                onChange={(value) => ModelActions.handleChangeModel('tokensPerContext', value)}
                                inputProps={{ step: 1 }}
                            />
                        </div>
                        <div>
                            <label className="block mb-2">
                                {i18n.translate({ id: 'title.temperature', sourceName: 'Model' })}
                            </label>
                            <div className="flex flex-col gap-3">
                                <Checkbox
                                    name="useTemperature"
                                    checked={model.temperature !== -1}
                                    label={i18n.translate({ id: 'checkbox.useTemperature', sourceName: 'Model' })}
                                    onChange={(e) => {
                                        if (!e.target.checked) {
                                            ModelActions.handleChangeModel('temperature', -1);
                                        } else {
                                            ModelActions.handleChangeModel('temperature', 1.0);
                                        }
                                    }}
                                />
                                <Input
                                    inputType="number"
                                    value={model.temperature === -1 ? '' : model.temperature ?? 1.0}
                                    onChange={(value) => {
                                        value = Number(value);
                                        if (value >= 0 && value <= 2) {
                                            ModelActions.handleChangeModel('temperature', value);
                                        } else {
                                            ModelActions.handleChangeModel('temperature', 1.0);
                                        }
                                    }}
                                    inputProps={{ step: 0.1, min: 0, max: 2, disabled: model.temperature === -1 }}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block mb-2">
                                {i18n.translate({ id: 'title.topP', sourceName: 'Model' })}
                            </label>
                            <div className="flex flex-col gap-3">
                                <Checkbox
                                    name="useTopP"
                                    checked={model.topP !== -1}
                                    label={i18n.translate({ id: 'checkbox.useTopP', sourceName: 'Model' })}
                                    onChange={(e) => {
                                        if (!e.target.checked) {
                                            ModelActions.handleChangeModel('topP', -1);
                                        } else {
                                            ModelActions.handleChangeModel('topP', 1.0);
                                        }
                                    }}
                                />
                                <Input
                                    inputType="number"
                                    value={model.topP === -1 ? '' : model.topP ?? 1.0}
                                    onChange={(value) => {
                                        value = Number(value);
                                        if (value >= 0 && value <= 2) {
                                            ModelActions.handleChangeModel('topP', value);
                                        } else {
                                            ModelActions.handleChangeModel('topP', 1.0);
                                        }
                                    }}
                                    inputProps={{ step: 0.1, min: 0.0, max: 1.0, disabled: model.topP === -1 }}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block mb-2">
                                {i18n.translate({ id: 'title.additionalParameters', sourceName: 'Model' })}
                            </label>
                            <div className="flex flex-col gap-3">
                                <TextareaAutosize
                                    className={`bg-input-backgroundColor w-full p-3 border border-2 ${
                                        jsonError ? 'border-red-500' : 'border-input-borderColor'
                                    } rounded-lg outline-none focus:border-primary transition-all duration-100`}
                                    value={additionalParamsText}
                                    onChange={(e) => {
                                        ModelActions.handleUpdateAdditionalParamsText(e.target.value);
                                    }}
                                    onBlur={() => {
                                        ModelActions.handleAdditionalParamsBlur();
                                    }}
                                    placeholder="{}"
                                    minRows={5}
                                />
                                {jsonError && <p className="text-sm text-red-500">{jsonError}</p>}
                                <p className="text-sm text-gray-500">
                                    {i18n.translate({ id: 'hint.additionalParameters', sourceName: 'Model' })}
                                </p>
                            </div>
                        </div>
                        <div>
                            <label className="block mb-2">
                                {i18n.translate({ id: 'title.reasoningEffort', sourceName: 'Model' })}
                            </label>
                            <div className="flex flex-col gap-3">
                                <Checkbox
                                    name="useReasoningEffort"
                                    checked={model.reasoningEffort !== 'none'}
                                    label={i18n.translate({ id: 'checkbox.useReasoningEffort', sourceName: 'Model' })}
                                    onChange={(e) => {
                                        if (!e.target.checked) {
                                            ModelActions.handleChangeModel('reasoningEffort', 'none');
                                        } else {
                                            ModelActions.handleChangeModel('reasoningEffort', 'medium');
                                        }
                                    }}
                                />
                                <Select
                                    value={model.reasoningEffort === 'none' ? 'medium' : model.reasoningEffort}
                                    onChange={(value) => ModelActions.handleChangeModel('reasoningEffort', value)}
                                    disabled={model.reasoningEffort === 'none'}
                                >
                                    <option value="minimal">
                                        {i18n.translate({ id: 'reasoningEffort.minimal', sourceName: 'Model' })}
                                    </option>
                                    <option value="low">
                                        {i18n.translate({ id: 'reasoningEffort.low', sourceName: 'Model' })}
                                    </option>
                                    <option value="medium">
                                        {i18n.translate({ id: 'reasoningEffort.medium', sourceName: 'Model' })}
                                    </option>
                                    <option value="high">
                                        {i18n.translate({ id: 'reasoningEffort.high', sourceName: 'Model' })}
                                    </option>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <label className="block mb-2">
                                {i18n.translate({ id: 'title.canStream', sourceName: 'Model' })}
                            </label>
                            <div className="flex flex-col gap-1 model-container">
                                <Checkbox
                                    name={model.identifier}
                                    checked={model.canStream ?? true}
                                    label={i18n.translate({ id: 'checkbox.canStream', sourceName: 'Model' })}
                                    onChange={(e) => ModelActions.handleChangeModel('canStream', e.target.checked)}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block mb-2">
                                {i18n.translate({ id: 'title.useStreamOptions', sourceName: 'Model' })}
                            </label>
                            <div className="flex flex-col gap-1 model-container">
                                <Checkbox
                                    name={model.identifier}
                                    checked={model.useStreamOptions ?? true}
                                    label={i18n.translate({ id: 'checkbox.useStreamOptions', sourceName: 'Model' })}
                                    onChange={(e) =>
                                        ModelActions.handleChangeModel('useStreamOptions', e.target.checked)
                                    }
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block mb-2">
                                {i18n.translate({ id: 'title.useUser', sourceName: 'Model' })}
                            </label>
                            <div className="flex flex-col gap-1 model-container">
                                <Checkbox
                                    name={model.identifier}
                                    checked={model.useUser ?? true}
                                    label={i18n.translate({ id: 'checkbox.useUser', sourceName: 'Model' })}
                                    onChange={(e) => ModelActions.handleChangeModel('useUser', e.target.checked)}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block mb-2">
                                {i18n.translate({ id: 'title.allowedMimeTypes', sourceName: 'Model' })}
                            </label>
                            <div
                                className="flex flex-col gap-1 model-container"
                                onPaste={(e) => {
                                    const text = e.clipboardData?.getData('text/plain') || '';
                                    const tokens = text
                                        .split(/[\n,;]+/)
                                        .map((t) => t.trim())
                                        .filter(Boolean);
                                    const isMultiple =
                                        tokens.length > 1 || (tokens.length === 1 && tokens[0].includes('/'));
                                    if (isMultiple) {
                                        e.preventDefault();
                                        handleMimePasteText(text);
                                    }
                                }}
                            >
                                <ReactSelect<MimeTypeOption, true>
                                    isMulti
                                    value={
                                        model.allowedMimeTypes?.map((mimeType: string) => ({
                                            value: mimeType,
                                            label: mimeType,
                                        })) || []
                                    }
                                    onChange={(
                                        newValue: MultiValue<MimeTypeOption>,
                                        actionMeta: ActionMeta<MimeTypeOption>,
                                    ) => {
                                        ModelActions.updateModel({
                                            allowedMimeTypes:
                                                newValue?.map((option: MimeTypeOption) => option.value) || [],
                                        });

                                        // Leere das Suchfeld sofort nach der Auswahl
                                        if (actionMeta.action === 'select-option') {
                                            const input = document.querySelector(
                                                '.select__input input',
                                            ) as HTMLInputElement;
                                            if (input) {
                                                input.value = '';
                                            }
                                        }
                                    }}
                                    options={Object.entries(allMimeTypesWithFileExtensions).map(
                                        ([mimeType, fileExtensions]) => ({
                                            value: mimeType,
                                            label: '.' + fileExtensions.join(', .') + ' (' + mimeType + ')',
                                        }),
                                    )}
                                    className="w-full"
                                    placeholder={
                                        i18n.translate({
                                            id: 'placeholder.selectMimeTypes',
                                            sourceName: 'Model',
                                        }) + i18n.translate({ id: 'copy.paste', sourceName: 'Model' })
                                    }
                                    noOptionsMessage={() => i18n.translate({ id: 'noOptions', sourceName: 'Model' })}
                                    isSearchable
                                    isClearable
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    classNames={{
                                        // Base Input
                                        control: () =>
                                            'min-h-[42px] bg-input-backgroundColor border-2 border-input-borderColor rounded-lg hover:border-primary focus-within:border-primary transition-all duration-100',
                                        // DropDown "Menu"
                                        menu: () =>
                                            'mt-1 bg-input-backgroundColor border-1 border-gray-700 rounded-lg shadow-lg',
                                        menuList: () => 'p-1',
                                        // Badge inside Input field
                                        multiValue: () =>
                                            'bg-gray-600 rounded text-white m-0.5 flex items-center max-w-[200px] sm:max-w-full truncate',
                                        multiValueLabel: () => 'px-1.5 py-0.5 text-white',
                                        multiValueRemove: () =>
                                            'px-1 rounded-r h-[28px] hover:bg-primary hover:text-layout-textColorOnPrimary transition-colors',
                                        input: () => 'text-layout-textcolor px-1',
                                        placeholder: () => 'text-gray-500 px-1',
                                        dropdownIndicator: () =>
                                            'p-1 hover:text-primary text-gray-500 rounded transition-colors',
                                        clearIndicator: () =>
                                            'p-1 hover:text-primary text-gray-500 rounded transition-colors',
                                        valueContainer: () => 'p-1 gap-1',
                                    }}
                                    unstyled
                                    components={{
                                        Option: ({ children, isSelected, innerProps, ...props }) => (
                                            <div
                                                {...innerProps}
                                                className={` flex items-center px-3 py-2 hover:bg-input-backgroundHoverColor rounded-md cursor-pointer text-white  ${
                                                    isSelected ? 'bg-input-backgroundHoverColor' : ''
                                                }`}
                                            >
                                                <div className="mr-2">
                                                    <Checkbox checked={isSelected} onChange={() => {}} />
                                                </div>
                                                <span className="text-layout-textcolor">{children}</span>
                                            </div>
                                        ),
                                    }}
                                />
                            </div>
                            <div
                                className="flex justify-start"
                                onClick={async () => {
                                    try {
                                        await navigator.clipboard.writeText(model.allowedMimeTypes.join(', '));
                                        ModelActions.setMimeTypesCopied(true);
                                        setTimeout(() => ModelActions.setMimeTypesCopied(false), 1500);
                                    } catch (err) {
                                        console.error('Failed to copy mime types:', err);
                                    }
                                }}
                            >
                                {model.allowedMimeTypes.length > 0 && (
                                    <div className="w-fit text-layout-textcolor hover:text-primary transition-colors duration-200 cursor-pointer">
                                        <div className="flex items-center gap-2">
                                            {mimeTypesCopied ? <Check size={16} /> : <Copy size={16} />}{' '}
                                            <span>
                                                {mimeTypesCopied
                                                    ? i18n.translate({ id: 'copy.success', sourceName: 'Model' })
                                                    : i18n.translate({ id: 'copy.title', sourceName: 'Model' })}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-col lg:flex-row gap-5">
                            <div className="flex-1">
                                <h5 className="mt-5">
                                    {i18n.translate({ id: 'basis.dlp.title', sourceName: 'Model' })}
                                </h5>
                                <h6>{i18n.translate({ id: 'gdpr.dlp.title', sourceName: 'Model' })}</h6>
                                <p>{i18n.translate({ id: 'gdpr.dlp.text', sourceName: 'Model' })}</p>
                                <div className="mt-5">
                                    <ToggleSwitch
                                        options={[
                                            {
                                                label: i18n.translate({ id: 'dlp.on', sourceName: 'Model' }),
                                                function: () => ModelActions.handleChangeModel('dlp', DLPState.dlp_on),
                                                active: model.dlp === DLPState.dlp_on,
                                            },
                                            {
                                                label: i18n.translate({ id: 'dlp.optional', sourceName: 'Model' }),
                                                function: () =>
                                                    ModelActions.handleChangeModel('dlp', DLPState.dlp_optional),
                                                active: model.dlp === DLPState.dlp_optional,
                                            },
                                            {
                                                label: i18n.translate({ id: 'dlp.off', sourceName: 'Model' }),
                                                function: () => ModelActions.handleChangeModel('dlp', DLPState.dlp_off),
                                                active: model.dlp === DLPState.dlp_off,
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                            <div
                                className={`flex-1 flex flex-col justify-between ${
                                    icapAddress ? '' : 'opacity-50 pointer-events-none'
                                }`}
                            >
                                <div>
                                    <h5 className="mt-5">
                                        {i18n.translate({
                                            id: 'enterprise.dlp.title',
                                            sourceName: 'Model',
                                        })}
                                    </h5>
                                    <h6>
                                        {i18n.translate({
                                            id: 'icap.dlp.title',
                                            sourceName: 'Model',
                                        })}
                                    </h6>
                                    <p>
                                        {i18n.translate({
                                            id: 'icap.dlp.text',
                                            sourceName: 'Model',
                                        })}
                                    </p>
                                </div>
                                <div className="mt-5 ">
                                    <ToggleSwitch
                                        options={[
                                            {
                                                label: i18n.translate({ id: 'dlp.on', sourceName: 'Model' }),
                                                function: () => ModelActions.handleChangeModel('icapDlpActive', true),
                                                active: model.icapDlpActive,
                                            },
                                            {
                                                label: i18n.translate({
                                                    id: 'dlp.off',
                                                    sourceName: 'Model',
                                                }),
                                                function: () => ModelActions.handleChangeModel('icapDlpActive', false),
                                                active: !model.icapDlpActive,
                                            },
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                        <div>
                            {sensitivityLabels.length > 0 && (
                                <>
                                    <h6 className="mt-5">
                                        {i18n.translate({ id: 'fileprotection.dlp.title', sourceName: 'Model' })}
                                    </h6>
                                    <p>{i18n.translate({ id: 'fileprotection.dlp.text', sourceName: 'Model' })}</p>

                                    <div className="mt-5">
                                        <ToggleSwitch
                                            options={[
                                                {
                                                    label: i18n.translate({
                                                        id: 'dlp.on',
                                                        sourceName: 'Model',
                                                    }),
                                                    function: () =>
                                                        ModelActions.handleChangeModel('sensitiveFileDlpActive', true),
                                                    active: model.sensitiveFileDlpActive,
                                                },
                                                {
                                                    label: i18n.translate({
                                                        id: 'dlp.off',
                                                        sourceName: 'Model',
                                                    }),
                                                    function: () => {
                                                        ModelActions.handleChangeModel('sensitiveFileDlpActive', false);
                                                        ModelActions.handleChangeModel('sensitivityLabels', []);
                                                    },
                                                    active: !model.sensitiveFileDlpActive,
                                                },
                                            ]}
                                        />
                                    </div>
                                    {model.sensitiveFileDlpActive && (
                                        <div className="mt-[20px] flex flex-col gap-1 model-container">
                                            <p>
                                                {i18n.translate({
                                                    id: 'fileprotection.dlp.classifications',
                                                    sourceName: 'Model',
                                                })}
                                            </p>
                                            {sensitivityLabels.map((sensitivityLabel: string) => {
                                                return (
                                                    <Checkbox
                                                        name={sensitivityLabel}
                                                        checked={model.sensitivityLabels.includes(sensitivityLabel)}
                                                        label={sensitivityLabel}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                ModelActions.updateModel({
                                                                    sensitivityLabels: [
                                                                        ...model.sensitivityLabels,
                                                                        sensitivityLabel,
                                                                    ],
                                                                });
                                                            } else {
                                                                ModelActions.updateModel({
                                                                    sensitivityLabels: model.sensitivityLabels.filter(
                                                                        (label) => label !== sensitivityLabel,
                                                                    ),
                                                                });
                                                            }
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="mt-5 text-right">
                            <Button variant={'primary'} onClick={ModelActions.handleSave}>
                                {i18n.translate({ id: 'save', sourceName: 'Main' })}
                            </Button>
                        </div>
                    </div>
                </AdminBox>
            </AdminContainer>
        </div>
    );
}

export default Model;
